import { useState, useMemo, useCallback, useEffect, useDeferredValue } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { CATEGORIES, COUNTRIES } from "../data/constants";
import { US_LOCATIONS } from "../data/usLocations";
import {
  MagnifyingGlass,
  Tag,
  Hash,
  ArrowLeft,
  X,
  CaretDown,
  EnvelopeSimple,
  Phone,
  Plus,
  Minus,
  FloppyDisk
} from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATE_CODES = Object.keys(US_LOCATIONS).sort();

const NewJobPage = () => {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const duplicateFrom = location.state?.duplicateFrom || null;
  const [loading, setLoading] = useState(false);
  const [categoriesDropdownOpen, setCategoriesDropdownOpen] = useState(false);
  const [locationDropdownOpen, setLocationDropdownOpen] = useState(false);
  const [categorySearch, setCategorySearch] = useState("");
  const [locationSearch, setLocationSearch] = useState("");
  // Defer the big list-filter inputs so typing stays snappy even while the
  // 4,000+ category filter and 50-state location tree re-render.
  const deferredCategorySearch = useDeferredValue(categorySearch);
  const deferredLocationSearch = useDeferredValue(locationSearch);
  const [expandedStates, setExpandedStates] = useState({});
  const [expandedCities, setExpandedCities] = useState({});
  const [showCustomCats, setShowCustomCats] = useState(false);
  const [customCatsInput, setCustomCatsInput] = useState("");
  const [showSelectedCatsOnly, setShowSelectedCatsOnly] = useState(false);
  const [showSelectedLocsOnly, setShowSelectedLocsOnly] = useState(false);
  const [categoryTags, setCategoryTags] = useState([]);
  const [saveTagName, setSaveTagName] = useState("");
  const [showSaveTag, setShowSaveTag] = useState(false);
  const [savingTag, setSavingTag] = useState(false);
  const [saveTagMode, setSaveTagMode] = useState("choose"); // "choose" | "new"
  // Snapshots freeze the list shown while "Show selected" is active so that
  // unchecking an item does NOT make it disappear — user can re-check if the
  // click was accidental. Populated on toggle-on, cleared on toggle-off.
  const [catsSnapshot, setCatsSnapshot] = useState([]);
  const [locsSnapshot, setLocsSnapshot] = useState([]);

  const toggleShowSelectedCats = () => {
    setShowSelectedCatsOnly(prev => {
      if (!prev) setCatsSnapshot([...formData.categories]);
      else setCatsSnapshot([]);
      return !prev;
    });
  };

  const toggleShowSelectedLocs = () => {
    setShowSelectedLocsOnly(prev => {
      if (!prev) setLocsSnapshot([...formData.selectedLocations]);
      else setLocsSnapshot([]);
      return !prev;
    });
  };

  // Parse a stored location string back into selectedLocations items
  const parseLocationString = (locStr) => {
    if (!locStr) return [];
    return locStr.split(";").map(s => s.trim()).filter(Boolean).map(item => {
      if (/^\d{4,5}$/.test(item)) return `ZIP:${item}`;
      if (item.includes(",")) return item; // "City, ST"
      if (STATE_CODES.includes(item.toUpperCase())) return item.toUpperCase();
      return item;
    });
  };

  const [formData, setFormData] = useState(() => {
    if (duplicateFrom) {
      const cats = (duplicateFrom.category || "")
        .split(",").map(c => c.trim()).filter(Boolean);
      return {
        name: duplicateFrom.name ? `${duplicateFrom.name} (Copy)` : "",
        categories: cats,
        selectedLocations: parseLocationString(duplicateFrom.location),
        country: duplicateFrom.country || "US",
        radius: duplicateFrom.radius || 5000,
        keywords: duplicateFrom.keywords || "",
        brandWhitelist: duplicateFrom.brand_whitelist || "",
        limit: duplicateFrom.limit || 0,
        enrich_emails: duplicateFrom.enrich_emails !== undefined ? duplicateFrom.enrich_emails : true,
        enrich_phones: duplicateFrom.enrich_phones !== undefined ? duplicateFrom.enrich_phones : false
      };
    }
    return {
      name: "",
      categories: [],
      selectedLocations: [],
      country: "US",
      radius: 5000,
      keywords: "",
      brandWhitelist: "",
      limit: 0,
      enrich_emails: true,
      enrich_phones: false
    };
  });

  useEffect(() => {
    if (duplicateFrom) {
      toast.success(`Pre-filled from "${duplicateFrom.name}". Edit and submit to create a new job.`);
    }
    // Fetch user's saved category tags
    (async () => {
      try {
        const res = await axios.get(`${API}/category-tags`, { headers: getAuthHeaders() });
        setCategoryTags(res.data || []);
      } catch { /* non-critical */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Category functions
  const toggleCategory = (category) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.includes(category)
        ? prev.categories.filter(c => c !== category)
        : [...prev.categories, category]
    }));
  };

  const removeCategory = (category) => {
    setFormData(prev => ({ ...prev, categories: prev.categories.filter(c => c !== category) }));
  };

  const filteredCategories = useMemo(() => {
    const q = deferredCategorySearch.toLowerCase();
    return CATEGORIES.filter(cat => {
      const matchesSearch = cat.toLowerCase().includes(q);
      if (showSelectedCatsOnly) {
        // Show items that were selected WHEN user toggled "Show selected" ON,
        // even if they have since been unchecked — so accidental unchecks stay visible.
        return matchesSearch && catsSnapshot.includes(cat);
      }
      return matchesSearch;
    });
  }, [deferredCategorySearch, showSelectedCatsOnly, catsSnapshot]);

  const clearAllCategories = () => {
    setFormData(prev => ({ ...prev, categories: [] }));
    setShowSelectedCatsOnly(false);
    setCatsSnapshot([]);
    toast.success("All categories cleared");
  };

  const loadCategoryTag = (tag) => {
    if (!tag?.categories?.length) return;
    setFormData(prev => {
      const merged = new Set(prev.categories);
      for (const c of tag.categories) merged.add(c);
      return { ...prev, categories: [...merged] };
    });
    toast.success(`Loaded "${tag.name}" (${tag.categories.length} categories)`);
  };

  const openSaveTag = () => {
    if (formData.categories.length === 0) {
      toast.error("Select at least one category first");
      return;
    }
    const ownedTags = categoryTags.filter(t => t.is_owner);
    setShowSaveTag(v => !v);
    setSaveTagMode(ownedTags.length > 0 ? "choose" : "new");
    setSaveTagName("");
  };

  const saveCategoriesAsTag = async () => {
    const name = saveTagName.trim();
    if (!name) {
      toast.error("Enter a tag name");
      return;
    }
    if (formData.categories.length === 0) {
      toast.error("Select at least one category first");
      return;
    }
    setSavingTag(true);
    try {
      const res = await axios.post(`${API}/category-tags`,
        { name, categories: formData.categories },
        { headers: getAuthHeaders() }
      );
      setCategoryTags(prev => [...prev, res.data].sort((a, b) => a.name.localeCompare(b.name)));
      setSaveTagName("");
      setShowSaveTag(false);
      toast.success(`Saved tag "${name}" with ${formData.categories.length} categories`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save tag");
    } finally {
      setSavingTag(false);
    }
  };

  const saveIntoExistingTag = async (tag) => {
    // Called from the "Save as tag → Overwrite existing" panel flow. User already
    // made an explicit choice so skip the extra window.confirm to avoid double-prompt.
    if (formData.categories.length === 0) {
      toast.error("Select at least one category first");
      return;
    }
    setSavingTag(true);
    try {
      const res = await axios.put(
        `${API}/category-tags/${tag.id}`,
        { categories: formData.categories },
        { headers: getAuthHeaders() }
      );
      setCategoryTags(prev => prev.map(t => t.id === res.data.id ? res.data : t)
                                  .sort((a, b) => a.name.localeCompare(b.name)));
      setShowSaveTag(false);
      toast.success(`Overwrote "${tag.name}" — now has ${res.data.categories.length} categories`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update tag");
    } finally {
      setSavingTag(false);
    }
  };

  const clearAllLocations = () => {
    setFormData(prev => ({ ...prev, selectedLocations: [] }));
    setShowSelectedLocsOnly(false);
    setLocsSnapshot([]);
    toast.success("All locations cleared");
  };

  // Bulk-paste locations. Reps paste "Alvin, Baytown, Bellville, ..." (or one
  // per line) and we match each name against every state's city list. If a
  // city name is unique across the US, we auto-add it. If it appears in
  // multiple states (e.g. "Springfield") we skip it silently — the rep can
  // pick the correct one via the tree dropdown. Also handles "City, ST"
  // format (e.g. "Springfield, IL") to disambiguate up-front.
  const [bulkLocationsInput, setBulkLocationsInput] = useState("");
  const handleBulkAddLocations = () => {
    const raw = bulkLocationsInput.trim();
    if (!raw) {
      toast.error("Paste one or more locations first.");
      return;
    }
    const tokens = raw.split(/[\n;]+|,(?=\s*[A-Za-z])/).map(t => t.trim()).filter(Boolean);
    let added = 0;
    let ambiguous = [];
    let unknown = [];
    setFormData(prev => {
      const set = new Set(prev.selectedLocations);
      for (const tok of tokens) {
        // "City, ST" explicit → straight lookup
        const parts = tok.split(",").map(p => p.trim());
        if (parts.length >= 2 && parts[1].length === 2) {
          const [city, st] = [parts[0], parts[1].toUpperCase()];
          if ((US_LOCATIONS[st] || {})[city]) {
            const key = `${city}, ${st}`;
            if (!set.has(key)) { set.add(key); added++; }
            continue;
          }
        }
        // Bare city name → search every state (case-insensitive)
        const wanted = tok.toLowerCase();
        const hits = [];
        for (const st of Object.keys(US_LOCATIONS)) {
          for (const city of Object.keys(US_LOCATIONS[st])) {
            if (city.toLowerCase() === wanted) hits.push({ city, st });
          }
        }
        if (hits.length === 1) {
          const key = `${hits[0].city}, ${hits[0].st}`;
          if (!set.has(key)) { set.add(key); added++; }
        } else if (hits.length > 1) {
          ambiguous.push(tok);
        } else {
          unknown.push(tok);
        }
      }
      return { ...prev, selectedLocations: [...set] };
    });
    if (added) toast.success(`Added ${added} location${added > 1 ? "s" : ""}.`);
    if (ambiguous.length) toast.error(`Ambiguous (add "City, ST" instead): ${ambiguous.slice(0, 5).join(", ")}${ambiguous.length > 5 ? "…" : ""}`, { duration: 8000 });
    if (unknown.length) toast.error(`Not found: ${unknown.slice(0, 5).join(", ")}${unknown.length > 5 ? "…" : ""}`, { duration: 8000 });
    setBulkLocationsInput("");
  };

  // Build case-insensitive lookup of CATEGORIES once
  const categoryLookup = useMemo(() => {
    const map = new Map();
    for (const c of CATEGORIES) map.set(c.toLowerCase(), c);
    return map;
  }, []);

  const addCustomCategories = () => {
    const raw = customCatsInput
      .split(/[,\n]/)
      .map(s => s.trim())
      .filter(Boolean);
    if (raw.length === 0) {
      toast.error("Paste one or more categories, separated by commas or new lines");
      return;
    }
    const matched = [];
    const unmatched = [];
    const seen = new Set();
    for (const item of raw) {
      const key = item.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      const canonical = categoryLookup.get(key);
      if (canonical) matched.push(canonical);
      else unmatched.push(item);
    }
    if (matched.length === 0) {
      toast.error(`No matching categories found. Unrecognized: ${unmatched.slice(0, 5).join(", ")}${unmatched.length > 5 ? "…" : ""}`);
      return;
    }
    setFormData(prev => {
      const merged = new Set(prev.categories);
      for (const c of matched) merged.add(c);
      return { ...prev, categories: [...merged] };
    });
    setCustomCatsInput("");
    if (unmatched.length > 0) {
      toast.success(`Added ${matched.length} categories. ${unmatched.length} not recognized: ${unmatched.slice(0, 3).join(", ")}${unmatched.length > 3 ? "…" : ""}`);
    } else {
      toast.success(`Added ${matched.length} categories`);
    }
  };

  // --- 3-Level Location Tree Logic ---

  const toggleExpandState = useCallback((stateCode) => {
    setExpandedStates(prev => ({ ...prev, [stateCode]: !prev[stateCode] }));
  }, []);

  const toggleExpandCity = useCallback((stateCode, city) => {
    const key = `${stateCode}::${city}`;
    setExpandedCities(prev => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const isStateSelected = useCallback((stateCode) => {
    return formData.selectedLocations.includes(stateCode);
  }, [formData.selectedLocations]);

  const isCitySelected = useCallback((city, stateCode) => {
    return formData.selectedLocations.includes(`${city}, ${stateCode}`);
  }, [formData.selectedLocations]);

  const isZipSelected = useCallback((zip) => {
    return formData.selectedLocations.includes(`ZIP:${zip}`);
  }, [formData.selectedLocations]);

  // Helper: get all keys under a state (city keys + zip keys)
  const getAllKeysForState = useCallback((stateCode) => {
    const cities = US_LOCATIONS[stateCode] || {};
    const keys = [];
    for (const city of Object.keys(cities)) {
      keys.push(`${city}, ${stateCode}`);
      for (const zip of cities[city]) {
        keys.push(`ZIP:${zip}`);
      }
    }
    return keys;
  }, []);

  // Helper: get all zip keys under a city
  const getAllZipKeysForCity = useCallback((city, stateCode) => {
    const zips = (US_LOCATIONS[stateCode] || {})[city] || [];
    return zips.map(z => `ZIP:${z}`);
  }, []);

  const toggleState = useCallback((stateCode) => {
    setFormData(prev => {
      const locs = new Set(prev.selectedLocations);
      if (locs.has(stateCode)) {
        // Deselect state + remove all children
        locs.delete(stateCode);
        for (const k of getAllKeysForState(stateCode)) locs.delete(k);
      } else {
        // Select state, remove individual children (state covers all)
        for (const k of getAllKeysForState(stateCode)) locs.delete(k);
        locs.add(stateCode);
      }
      return { ...prev, selectedLocations: [...locs] };
    });
  }, [getAllKeysForState]);

  const toggleCity = useCallback((city, stateCode) => {
    setFormData(prev => {
      const locs = new Set(prev.selectedLocations);
      const cityKey = `${city}, ${stateCode}`;

      // Breakout: if state is selected, expand to all sibling cities and drop this one
      if (locs.has(stateCode)) {
        locs.delete(stateCode);
        const allCities = Object.keys(US_LOCATIONS[stateCode] || {});
        for (const c of allCities) {
          if (c !== city) locs.add(`${c}, ${stateCode}`);
        }
        return { ...prev, selectedLocations: [...locs] };
      }

      if (locs.has(cityKey)) {
        // Deselect city + its zips
        locs.delete(cityKey);
        for (const zk of getAllZipKeysForCity(city, stateCode)) locs.delete(zk);
      } else {
        // Select city, remove individual zip selections
        for (const zk of getAllZipKeysForCity(city, stateCode)) locs.delete(zk);
        locs.add(cityKey);
      }
      return { ...prev, selectedLocations: [...locs] };
    });
  }, [getAllZipKeysForCity]);

  const toggleZip = useCallback((zip, city, stateCode) => {
    setFormData(prev => {
      const locs = new Set(prev.selectedLocations);
      const cityKey = `${city}, ${stateCode}`;
      const zipKey = `ZIP:${zip}`;

      // Breakout: state selected → expand to cities, then expand this city to zips
      if (locs.has(stateCode)) {
        locs.delete(stateCode);
        const allCities = Object.keys(US_LOCATIONS[stateCode] || {});
        for (const c of allCities) {
          if (c !== city) locs.add(`${c}, ${stateCode}`);
        }
        const allZips = getAllZipKeysForCity(city, stateCode);
        for (const zk of allZips) {
          if (zk !== zipKey) locs.add(zk);
        }
        return { ...prev, selectedLocations: [...locs] };
      }

      // Breakout: city selected → expand to sibling zips and drop this one
      if (locs.has(cityKey)) {
        locs.delete(cityKey);
        const allZips = getAllZipKeysForCity(city, stateCode);
        for (const zk of allZips) {
          if (zk !== zipKey) locs.add(zk);
        }
        return { ...prev, selectedLocations: [...locs] };
      }

      if (locs.has(zipKey)) {
        locs.delete(zipKey);
      } else {
        locs.add(zipKey);
      }
      return { ...prev, selectedLocations: [...locs] };
    });
  }, [getAllZipKeysForCity]);

  const removeLocation = (loc) => {
    setFormData(prev => ({ ...prev, selectedLocations: prev.selectedLocations.filter(l => l !== loc) }));
  };

  // Filter states/cities/zips based on search
  const filteredLocationData = useMemo(() => {
    const q = deferredLocationSearch.toLowerCase().trim();
    // When in "Show selected" mode, filter against the frozen snapshot so
    // unchecked items stay visible for easy re-check.
    const selectedSet = new Set(showSelectedLocsOnly ? locsSnapshot : formData.selectedLocations);

    const baseList = () => STATE_CODES.map(sc => ({
      code: sc,
      cities: Object.keys(US_LOCATIONS[sc] || {}).sort()
        .map(city => ({ name: city, zips: US_LOCATIONS[sc][city] }))
    }));

    const applySelectedFilter = (list) => {
      if (!showSelectedLocsOnly) return list;
      const filtered = [];
      for (const { code, cities } of list) {
        const stateFullySelected = selectedSet.has(code);
        const citiesInState = [];
        for (const { name: city, zips } of cities) {
          const cityKey = `${city}, ${code}`;
          const citySelected = stateFullySelected || selectedSet.has(cityKey);
          const zipMatches = zips.filter(z => selectedSet.has(`ZIP:${z}`));
          if (citySelected) {
            citiesInState.push({ name: city, zips });
          } else if (zipMatches.length > 0) {
            citiesInState.push({ name: city, zips: zipMatches });
          }
        }
        if (stateFullySelected || citiesInState.length > 0) {
          filtered.push({ code, cities: stateFullySelected ? cities : citiesInState });
        }
      }
      return filtered;
    };

    if (!q) {
      return applySelectedFilter(baseList());
    }

    const isZipSearch = /^\d+$/.test(q);
    const result = [];

    for (const sc of STATE_CODES) {
      const stateData = US_LOCATIONS[sc] || {};
      const stateMatches = sc.toLowerCase().includes(q);
      const matchedCities = [];

      for (const city of Object.keys(stateData).sort()) {
        const cityMatches = city.toLowerCase().includes(q);
        const zips = stateData[city];
        const matchedZips = isZipSearch ? zips.filter(z => z.startsWith(q)) : [];

        if (stateMatches || cityMatches || matchedZips.length > 0) {
          matchedCities.push({
            name: city,
            zips: (cityMatches || stateMatches) ? zips : matchedZips
          });
        }
      }

      if (stateMatches || matchedCities.length > 0) {
        result.push({
          code: sc,
          cities: stateMatches
            ? Object.keys(stateData).sort().map(c => ({ name: c, zips: stateData[c] }))
            : matchedCities
        });
      }
    }
    return applySelectedFilter(result);
  }, [deferredLocationSearch, showSelectedLocsOnly, formData.selectedLocations, locsSnapshot]);

  const selectedCount = formData.selectedLocations.length;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || formData.categories.length === 0) {
      toast.error("Please fill in job name and select at least one category");
      return;
    }

    if (!formData.enrich_emails && !formData.enrich_phones) {
      toast.error("Pick at least one enrichment — Email or Phone (or both).");
      return;
    }

    setLoading(true);
    try {
      // Build location string - strip ZIP: prefix for backend
      const locationStr = formData.selectedLocations
        .map(loc => loc.startsWith("ZIP:") ? loc.replace("ZIP:", "") : loc)
        .join("; ");
      const response = await axios.post(`${API}/jobs`, {
        name: formData.name,
        category: formData.categories.join(", "),
        location: locationStr,
        country: formData.country,
        radius: formData.radius,
        keywords: formData.keywords,
        brand_whitelist: formData.brandWhitelist || "",
        limit: formData.limit,
        enrich_emails: formData.enrich_emails,
        enrich_phones: formData.enrich_phones
      }, {
        headers: getAuthHeaders()
      });
      const enrichBits = [];
      if (formData.enrich_emails) enrichBits.push("email");
      if (formData.enrich_phones) enrichBits.push("phone");
      toast.success(`Job created! Processing will start with ${enrichBits.join(" + ")} enrichment.`);
      navigate(`/jobs/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create job");
    } finally {
      setLoading(false);
    }
  };

  // Display label for a selected location
  const locLabel = (loc) => {
    if (loc.startsWith("ZIP:")) return loc.replace("ZIP:", "") + " (zip)";
    return loc;
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="new-job-page">
      {/* Category Selection Modal */}
      {categoriesDropdownOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-4" onClick={() => setCategoriesDropdownOpen(false)}>
          <div
            className="bg-white border border-slate-200 rounded-lg shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-slate-200 flex-shrink-0">
              <h3 className="text-lg font-bold text-slate-900">Select Categories</h3>
              <button type="button" onClick={() => setCategoriesDropdownOpen(false)} className="text-slate-500 hover:text-slate-900">
                <X size={20} />
              </button>
            </div>
            <div className="p-4 border-b border-slate-200 space-y-3 flex-shrink-0">
              <Input
                value={categorySearch}
                onChange={(e) => setCategorySearch(e.target.value)}
                placeholder="Search categories..."
                className="bg-white/50 border-slate-200 text-slate-900"
                data-testid="category-search-input"
              />
              <div className="flex items-center gap-3 flex-wrap">
                <button
                  type="button"
                  onClick={() => setShowCustomCats(v => !v)}
                  className="text-xs text-[#0EA5E9] hover:text-[#0284C7] font-medium flex items-center gap-1"
                  data-testid="toggle-custom-cats-btn"
                >
                  {showCustomCats ? "− Hide" : "+ Paste custom list"}
                </button>
                <button
                  type="button"
                  onClick={openSaveTag}
                  className="text-xs text-[#006994] hover:text-[#005A80] font-medium flex items-center gap-1"
                  data-testid="toggle-save-tag-btn"
                >
                  {showSaveTag ? "− Hide save" : "★ Save as tag"}
                </button>
                {formData.categories.length > 0 && (
                  <button
                    type="button"
                    onClick={toggleShowSelectedCats}
                    className={`text-xs font-medium flex items-center gap-1 ${
                      showSelectedCatsOnly ? 'text-[#006994]' : 'text-slate-500 hover:text-slate-700'
                    }`}
                    data-testid="toggle-show-selected-cats-btn"
                  >
                    {showSelectedCatsOnly ? `✓ Showing ${catsSnapshot.length} selected` : `Show ${formData.categories.length} selected`}
                  </button>
                )}
              </div>
              {showSaveTag && (
                <div
                  className="rounded-lg border border-[#006994]/30 bg-[#006994]/5 p-3 space-y-3"
                  data-testid="save-tag-box"
                >
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-semibold text-[#006994]">
                      Save {formData.categories.length} selected as a tag
                    </p>
                    <button
                      type="button"
                      onClick={() => setShowSaveTag(false)}
                      className="text-slate-500 hover:text-slate-700"
                    >
                      <X size={14} />
                    </button>
                  </div>

                  {categoryTags.some(t => t.is_owner) && (
                    <div className="flex items-center gap-1 p-0.5 rounded-md bg-white/60 border border-slate-200">
                      <button
                        type="button"
                        onClick={() => setSaveTagMode("choose")}
                        className={`flex-1 px-2.5 py-1.5 rounded text-xs font-medium transition-colors ${
                          saveTagMode === "choose"
                            ? "bg-[#006994]/20 text-[#006994]"
                            : "text-slate-500 hover:text-slate-700"
                        }`}
                        data-testid="save-mode-choose-btn"
                      >
                        Overwrite mine
                      </button>
                      <button
                        type="button"
                        onClick={() => setSaveTagMode("new")}
                        className={`flex-1 px-2.5 py-1.5 rounded text-xs font-medium transition-colors ${
                          saveTagMode === "new"
                            ? "bg-[#006994]/20 text-[#006994]"
                            : "text-slate-500 hover:text-slate-700"
                        }`}
                        data-testid="save-mode-new-btn"
                      >
                        Create new
                      </button>
                    </div>
                  )}

                  {saveTagMode === "choose" && categoryTags.some(t => t.is_owner) && (
                    <div className="space-y-1 max-h-40 overflow-y-auto">
                      {categoryTags.filter(t => t.is_owner).map(tag => (
                        <button
                          key={tag.id}
                          type="button"
                          onClick={() => {
                            if (window.confirm(`Overwrite "${tag.name}" with the current ${formData.categories.length} selected categories? The existing ${tag.categories.length} will be replaced.`)) {
                              saveIntoExistingTag(tag);
                            }
                          }}
                          disabled={savingTag}
                          className="w-full text-left px-3 py-2 rounded-md border border-slate-200 hover:border-[#006994]/50 hover:bg-[#006994]/10 transition-colors flex items-center justify-between disabled:opacity-50"
                          data-testid={`save-into-tag-${tag.id}`}
                        >
                          <div>
                            <p className="text-xs font-medium text-slate-700">{tag.name}</p>
                            <p className="text-[10px] text-slate-500 mt-0.5">
                              Currently has {tag.categories.length} categories
                            </p>
                          </div>
                          <FloppyDisk size={14} className="text-[#006994]" />
                        </button>
                      ))}
                    </div>
                  )}

                  {saveTagMode === "new" && (
                    <div className="flex items-center gap-2">
                      <Input
                        value={saveTagName}
                        onChange={(e) => setSaveTagName(e.target.value)}
                        placeholder="New tag name (e.g., Legal Pros)"
                        maxLength={60}
                        className="bg-white/50 border-slate-200 text-slate-900 text-sm flex-1"
                        data-testid="save-tag-input"
                        onKeyDown={(e) => { if (e.key === "Enter") saveCategoriesAsTag(); }}
                      />
                      <Button
                        type="button"
                        onClick={saveCategoriesAsTag}
                        disabled={savingTag || !saveTagName.trim()}
                        size="sm"
                        className="bg-[#006994] hover:bg-[#005A80] text-white text-xs font-bold"
                        data-testid="save-tag-submit-btn"
                      >
                        {savingTag ? "Saving..." : "Create"}
                      </Button>
                    </div>
                  )}
                </div>
              )}
              {categoryTags.length > 0 && (
                <div className="space-y-1.5">
                  <p className="text-[11px] text-slate-500 uppercase tracking-wider">
                    Saved tags <span className="normal-case text-slate-400">— click to load. Manage or delete your own tags in Settings.</span>
                  </p>
                  <div className="flex flex-wrap gap-1.5" data-testid="category-tags-list">
                    {categoryTags.map(tag => (
                      <button
                        key={tag.id}
                        type="button"
                        onClick={() => loadCategoryTag(tag)}
                        className={`px-2.5 py-1 rounded-full border text-xs font-medium transition-colors ${
                          tag.is_owner
                            ? "bg-[#006994]/10 border-[#006994]/30 text-[#006994] hover:bg-[#006994]/20"
                            : "bg-slate-100/40 border-slate-200 text-slate-700 hover:bg-zinc-700/50"
                        }`}
                        data-testid={`load-tag-${tag.id}`}
                        title={`Load ${tag.categories.length} categories${tag.is_owner ? "" : ` (created by ${tag.owner_name})`}`}
                      >
                        {tag.name}
                        <span className={`ml-1 ${tag.is_owner ? "text-[#006994]/60" : "text-slate-500"}`}>
                          ({tag.categories.length})
                        </span>
                        {!tag.is_owner && (
                          <span className="ml-1.5 text-[10px] text-slate-500 italic">· {tag.owner_name}</span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {showCustomCats && (
                <div className="space-y-2">
                  <Textarea
                    value={customCatsInput}
                    onChange={(e) => setCustomCatsInput(e.target.value)}
                    placeholder="e.g., Law Firm, Dentist, Plumber, Accountant"
                    rows={3}
                    className="bg-white/50 border-slate-200 text-slate-900 text-sm resize-none"
                    data-testid="custom-cats-input"
                  />
                  <div className="flex items-center justify-between">
                    <p className="text-[11px] text-slate-500">Only categories that match our list will be added. Unknown ones are listed in the toast.</p>
                    <Button
                      type="button"
                      onClick={addCustomCategories}
                      size="sm"
                      className="bg-[#0EA5E9] hover:bg-[#0284C7] text-slate-900 text-xs font-bold"
                      data-testid="add-custom-cats-btn"
                    >
                      Add
                    </Button>
                  </div>
                </div>
              )}
            </div>
            <div className="overflow-y-auto flex-1 min-h-[200px]">
              {filteredCategories.map(cat => (
                <div
                  key={cat}
                  className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer border-b border-slate-200 transition-colors ${
                    formData.categories.includes(cat)
                      ? 'bg-[#0EA5E9]/10 text-[#0EA5E9]'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                  onClick={() => toggleCategory(cat)}
                >
                  <div className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center ${
                    formData.categories.includes(cat)
                      ? 'bg-[#0EA5E9] border-[#0EA5E9]'
                      : 'border-zinc-600 bg-slate-100'
                  }`}>
                    {formData.categories.includes(cat) && (
                      <svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    )}
                  </div>
                  <span className="text-sm">{cat}</span>
                </div>
              ))}
              {filteredCategories.length === 0 && (
                <div className="px-4 py-8 text-center text-slate-500 text-sm">
                  {showSelectedCatsOnly ? "No selections match your search" : "No categories match your search"}
                </div>
              )}
            </div>
            <div className="p-4 border-t border-slate-200 flex justify-between items-center flex-shrink-0">
              <div className="flex items-center gap-3">
                <span className="text-sm text-slate-500">{formData.categories.length} selected</span>
                {formData.categories.length > 0 && (
                  <button
                    type="button"
                    onClick={clearAllCategories}
                    className="text-xs text-red-400 hover:text-red-300 font-medium"
                    data-testid="clear-all-cats-btn"
                  >
                    Clear all
                  </button>
                )}
              </div>
              <Button type="button" onClick={() => setCategoriesDropdownOpen(false)} className="bg-[#0EA5E9] hover:bg-[#0284C7] text-slate-900 font-bold">
                Done
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Location Selection Modal - 3-Level Tree */}
      {locationDropdownOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-4" onClick={() => setLocationDropdownOpen(false)}>
          <div
            className="bg-white border border-slate-200 rounded-lg shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-slate-200 flex-shrink-0">
              <h3 className="text-lg font-bold text-slate-900">Select states/jurisdictions/cities</h3>
              <button type="button" onClick={() => setLocationDropdownOpen(false)} className="text-slate-500 hover:text-slate-900">
                <X size={20} />
              </button>
            </div>
            <div className="p-4 border-b border-slate-200 space-y-3 flex-shrink-0">
              <div className="relative">
                <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                <Input
                  value={locationSearch}
                  onChange={(e) => setLocationSearch(e.target.value)}
                  placeholder="Search states, cities, or zip codes..."
                  className="pl-9 bg-white/50 border-slate-200 text-slate-900"
                  data-testid="location-search-input"
                />
              </div>
              {formData.selectedLocations.length > 0 && (
                <button
                  type="button"
                  onClick={toggleShowSelectedLocs}
                  className={`text-xs font-medium flex items-center gap-1 ${
                    showSelectedLocsOnly ? 'text-[#006994]' : 'text-slate-500 hover:text-slate-700'
                  }`}
                  data-testid="toggle-show-selected-locs-btn"
                >
                  {showSelectedLocsOnly ? `✓ Showing ${locsSnapshot.length} selected` : `Show ${formData.selectedLocations.length} selected`}
                </button>
              )}
            </div>
            <div className="overflow-y-auto flex-1 min-h-[200px]" data-testid="location-tree">
              {(() => {
                // Only auto-expand cities→zips when the user is explicitly searching a zip code
                // (numeric query). Otherwise a 1-char search would render 40k+ zip rows → jank.
                const q = deferredLocationSearch.trim();
                const autoExpandCities = /^\d+$/.test(q);
                return filteredLocationData.map(({ code, cities }) => {
                const stateExpanded = expandedStates[code] || !!deferredLocationSearch;
                const stateChecked = isStateSelected(code);
                return (
                  <div key={code}>
                    {/* State row */}
                    <div className="flex items-center border-b border-slate-200/30">
                      <button
                        type="button"
                        onClick={() => toggleExpandState(code)}
                        className="flex items-center justify-center w-8 h-10 text-slate-500 hover:text-slate-700 flex-shrink-0"
                        data-testid={`expand-${code}`}
                      >
                        {stateExpanded ? <Minus size={14} weight="bold" /> : <Plus size={14} weight="bold" />}
                      </button>
                      <div
                        className={`flex items-center gap-2 flex-1 py-2.5 pr-4 cursor-pointer transition-colors ${
                          stateChecked ? 'text-[#006994]' : 'text-slate-700 hover:text-slate-900'
                        }`}
                        onClick={() => toggleState(code)}
                      >
                        <div className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center ${
                          stateChecked ? 'bg-[#006994] border-[#006994]' : 'border-zinc-600 bg-slate-100'
                        }`}>
                          {stateChecked && (
                            <svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4L3.5 6.5L9 1" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                          )}
                        </div>
                        <span className="text-sm font-medium">{code}</span>
                      </div>
                    </div>
                    {/* Cities (level 2) */}
                    {stateExpanded && cities.map(({ name: city, zips }) => {
                      const cityKey = `${code}::${city}`;
                      const cityExpanded = expandedCities[cityKey] || autoExpandCities;
                      const cityChecked = stateChecked || isCitySelected(city, code);
                      return (
                        <div key={cityKey}>
                          {/* City row */}
                          <div className="flex items-center border-b border-slate-200/20">
                            <div className="w-8 flex-shrink-0" /> {/* indent */}
                            <button
                              type="button"
                              onClick={() => toggleExpandCity(code, city)}
                              className="flex items-center justify-center w-7 h-9 text-slate-400 hover:text-slate-500 flex-shrink-0"
                              data-testid={`expand-city-${code}-${city.replace(/\s/g, '_')}`}
                            >
                              {cityExpanded ? <Minus size={12} weight="bold" /> : <Plus size={12} weight="bold" />}
                            </button>
                            <div
                              className={`flex items-center gap-2 flex-1 py-2 pr-4 cursor-pointer transition-colors ${
                                cityChecked
                                  ? 'text-[#006994]'
                                  : 'text-slate-500 hover:text-slate-700'
                              }`}
                              onClick={() => toggleCity(city, code)}
                            >
                              <div className={`w-3.5 h-3.5 rounded border flex-shrink-0 flex items-center justify-center ${
                                cityChecked ? 'bg-[#006994] border-[#006994]' : 'border-zinc-600 bg-slate-100'
                              }`}>
                                {cityChecked && (
                                  <svg width="8" height="6" viewBox="0 0 10 8" fill="none"><path d="M1 4L3.5 6.5L9 1" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                )}
                              </div>
                              <span className="text-xs">{city}</span>
                            </div>
                          </div>
                          {/* Zip codes (level 3) */}
                          {cityExpanded && zips.map(zip => {
                            const zipChecked = stateChecked || isCitySelected(city, code) || isZipSelected(zip);
                            return (
                              <div
                                key={`${cityKey}-${zip}`}
                                className={`flex items-center gap-2 border-b border-slate-200/10 transition-colors cursor-pointer ${
                                  zipChecked
                                    ? 'text-[#006994] bg-[#006994]/5'
                                    : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100/20'
                                }`}
                                style={{ paddingLeft: '4.5rem' }}
                                onClick={() => toggleZip(zip, city, code)}
                              >
                                <div className="w-2 border-l border-slate-200/50 h-full" />
                                <div className={`w-3 h-3 rounded border flex-shrink-0 flex items-center justify-center ${
                                  zipChecked ? 'bg-[#006994] border-[#006994]' : 'border-slate-200 bg-slate-100'
                                }`}>
                                  {zipChecked && (
                                    <svg width="7" height="5" viewBox="0 0 10 8" fill="none"><path d="M1 4L3.5 6.5L9 1" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                  )}
                                </div>
                                <span className="text-[11px] py-1.5">{zip} <span className="text-slate-400">zip code</span></span>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                );
              });
              })()}
              {filteredLocationData.length === 0 && (
                <div className="px-4 py-8 text-center text-slate-500 text-sm">
                  {showSelectedLocsOnly ? "No selections match your search" : "No locations match your search"}
                </div>
              )}
            </div>
            <div className="p-4 border-t border-slate-200 flex justify-between items-center flex-shrink-0">
              <div className="flex items-center gap-3">
                <span className="text-sm text-slate-500">{selectedCount} selected</span>
                {selectedCount > 0 && (
                  <button
                    type="button"
                    onClick={clearAllLocations}
                    className="text-xs text-red-400 hover:text-red-300 font-medium"
                    data-testid="clear-all-locs-btn"
                  >
                    Clear all
                  </button>
                )}
              </div>
              <Button type="button" onClick={() => setLocationDropdownOpen(false)} className="bg-[#006994] hover:bg-[#005A80] text-white font-bold">
                Done
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate("/dashboard")} className="text-slate-500 hover:text-slate-900" data-testid="back-btn">
          <ArrowLeft size={20} />
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
            {duplicateFrom ? "Duplicate Lead Job" : "New Lead Job"}
          </h1>
          <p className="text-slate-500 mt-1">
            {duplicateFrom
              ? `Pre-filled from "${duplicateFrom.name}" — edit and submit to create a new job`
              : "Configure your lead search parameters"}
          </p>
        </div>
      </div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        <Card className="glass-card border-slate-200">
          <CardHeader>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <CardTitle className="text-lg text-slate-900">Search Parameters</CardTitle>
                <CardDescription className="text-slate-500">
                  Define the criteria for finding business leads from Google Maps
                </CardDescription>
              </div>
              <button
                type="button"
                onClick={() => navigate("/jobs/upload")}
                className="text-xs text-[#006994] hover:underline inline-flex items-center gap-1 mt-1"
                data-testid="goto-upload-btn"
              >
                Already have a list? Upload CSV →
              </button>
              <button
                type="button"
                onClick={() => navigate("/jobs/firecrawl")}
                className="text-xs text-[#006994] hover:underline inline-flex items-center gap-1 mt-1"
                data-testid="goto-firecrawl-btn"
              >
                Have a URL? Fire Crawl →
              </button>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Job Name */}
              <div className="space-y-2">
                <Label className="text-slate-700">Job Name *</Label>
                <div className="relative">
                  <Tag className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                  <Input
                    value={formData.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    placeholder="e.g., Dentists All States Q1 2024"
                    className="pl-10 bg-white/50 border-slate-200 focus:border-[#006994] focus:ring-1 focus:ring-[#006994] text-slate-900 placeholder:text-slate-400"
                    data-testid="job-name-input"
                  />
                </div>
              </div>

              {/* Multi-Select Categories */}
              <div className="space-y-2">
                <Label className="text-slate-700">Business Categories * (Select Multiple)</Label>
                <button
                  type="button"
                  onClick={() => setCategoriesDropdownOpen(!categoriesDropdownOpen)}
                  className="w-full flex items-center justify-between px-3 py-2 bg-white/50 border border-slate-200 rounded-md text-left focus:border-[#006994] focus:ring-1 focus:ring-[#006994]"
                  data-testid="categories-dropdown-btn"
                >
                  <div className="flex flex-wrap gap-1 flex-1">
                    {formData.categories.length > 0 ? (
                      formData.categories.slice(0, 3).map(cat => (
                        <span key={cat} className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#0EA5E9]/10 text-[#0EA5E9] rounded text-xs font-medium">
                          {cat}
                          <X size={12} className="cursor-pointer hover:text-slate-900" onClick={(e) => { e.stopPropagation(); removeCategory(cat); }} />
                        </span>
                      ))
                    ) : (
                      <span className="text-slate-500">Click to select categories...</span>
                    )}
                    {formData.categories.length > 3 && (
                      <span className="text-slate-500 text-xs">+{formData.categories.length - 3} more</span>
                    )}
                  </div>
                  <CaretDown size={16} className="text-slate-500 ml-2" />
                </button>
                {formData.categories.length > 0 && (
                  <p className="text-xs text-[#0EA5E9]">{formData.categories.length} category(s) selected</p>
                )}
              </div>

              {/* Country */}
              <div className="space-y-2">
                <Label className="text-slate-700">Country</Label>
                <Select value={formData.country} onValueChange={(v) => handleChange("country", v)}>
                  <SelectTrigger className="bg-white/50 border-slate-200 focus:border-[#006994] focus:ring-1 focus:ring-[#006994] text-slate-900" data-testid="country-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">
                    {COUNTRIES.map((c) => (
                      <SelectItem key={c.code} value={c.code} className="text-slate-900 hover:bg-slate-100">
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Location Tree Selector */}
              <div className="space-y-2">
                <Label className="text-slate-700">Locations</Label>
                <button
                  type="button"
                  onClick={() => setLocationDropdownOpen(true)}
                  className="w-full flex items-center justify-between px-3 py-2 bg-white/50 border border-slate-200 rounded-md text-left focus:border-[#006994] focus:ring-1 focus:ring-[#006994]"
                  data-testid="location-dropdown-btn"
                >
                  <div className="flex flex-wrap gap-1 flex-1">
                    {formData.selectedLocations.length > 0 ? (
                      formData.selectedLocations.slice(0, 4).map(loc => (
                        <span key={loc} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                          loc.startsWith("ZIP:")
                            ? 'bg-emerald-500/10 text-emerald-400'
                            : 'bg-[#006994]/10 text-[#006994]'
                        }`}>
                          {locLabel(loc)}
                          <X size={12} className="cursor-pointer hover:text-slate-900" onClick={(e) => { e.stopPropagation(); removeLocation(loc); }} />
                        </span>
                      ))
                    ) : (
                      <span className="text-slate-500">Select states/jurisdictions/cities</span>
                    )}
                    {formData.selectedLocations.length > 4 && (
                      <span className="text-slate-500 text-xs">+{formData.selectedLocations.length - 4} more</span>
                    )}
                  </div>
                  <CaretDown size={16} className="text-slate-500 ml-2" />
                </button>

                {/* Bulk-paste bar */}
                <div className="flex items-start gap-2 mt-2" data-testid="bulk-locations-row">
                  <textarea
                    value={bulkLocationsInput}
                    onChange={(e) => setBulkLocationsInput(e.target.value)}
                    rows={2}
                    placeholder="Bulk paste — Alvin, Baytown, Bellville, ..."
                    className="flex-1 bg-white border border-slate-200 rounded-md px-3 py-2 text-sm text-slate-900 focus:outline-none focus:border-[#006994]"
                    data-testid="bulk-locations-input"
                  />
                  <button
                    type="button"
                    onClick={handleBulkAddLocations}
                    className="px-3 py-2 rounded-md bg-[#006994] hover:bg-[#005A80] text-white text-xs font-semibold whitespace-nowrap"
                    data-testid="bulk-locations-add"
                  >
                    Add locations
                  </button>
                </div>
                <p className="text-[11px] text-slate-500 mt-1">
                  Ambiguous names (e.g. Springfield) — use <code className="bg-slate-100 px-1 rounded">City, ST</code>.
                </p>

                {selectedCount > 0 && (
                  <p className="text-xs text-[#006994]">{selectedCount} location(s) selected</p>
                )}
              </div>

              {/* Lead Limit */}
              <div className="space-y-2">
                <Label className="text-slate-700">Maximum results limit (0 = unlimited)</Label>
                <div className="relative">
                  <Hash className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                  <Input
                    type="number"
                    value={formData.limit}
                    onChange={(e) => handleChange("limit", e.target.value)}
                    onBlur={(e) => handleChange("limit", e.target.value === "" ? 0 : parseInt(e.target.value) || 0)}
                    min={0}
                    className="pl-10 bg-white/50 border-slate-200 focus:border-[#006994] focus:ring-1 focus:ring-[#006994] text-slate-900"
                    data-testid="limit-input"
                  />
                </div>
              </div>

              {/* Email Enrichment Toggle */}
              <div className="p-4 rounded-lg bg-[#0EA5E9]/5 border border-[#0EA5E9]/20">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.enrich_emails}
                    onChange={(e) => handleChange("enrich_emails", e.target.checked)}
                    className="w-5 h-5 rounded border-[#0EA5E9]/50 bg-slate-100 text-[#0EA5E9] focus:ring-[#0EA5E9] focus:ring-offset-0 accent-[#0EA5E9]"
                    data-testid="enrich-emails-checkbox"
                  />
                  <div>
                    <div className="flex items-center gap-2">
                      <EnvelopeSimple size={18} className="text-[#0EA5E9]" />
                      <span className="text-slate-900 font-medium">Enable Email Enrichment</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      Use the LeadFlow Scraper email finder to enrich missing email addresses from business websites — for cold-email campaigns (Instantly).
                    </p>
                  </div>
                </label>
              </div>

              {/* Phone Enrichment Toggle */}
              <div className="p-4 rounded-lg bg-[#006994]/5 border border-[#006994]/20">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.enrich_phones}
                    onChange={(e) => handleChange("enrich_phones", e.target.checked)}
                    className="w-5 h-5 rounded border-[#006994]/50 bg-slate-100 text-[#006994] focus:ring-[#006994] focus:ring-offset-0 accent-[#006994]"
                    data-testid="enrich-phones-checkbox"
                  />
                  <div>
                    <div className="flex items-center gap-2">
                      <Phone size={18} className="text-[#006994]" />
                      <span className="text-slate-900 font-medium">Enable Phone Enrichment</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      Validate &amp; normalise phone numbers (E.164) — for cold-call / voice drop / SMS campaigns (GHL). No extra API cost.
                    </p>
                  </div>
                </label>
              </div>

              <p className="text-xs text-slate-500 -mt-1 ml-1">
                Pick at least one. Tick both to keep leads with either email or phone.
              </p>

              {/* Keywords */}
              <div className="space-y-2">
                <Label className="text-slate-700">Additional Keywords (Optional)</Label>
                <Textarea
                  value={formData.keywords}
                  onChange={(e) => handleChange("keywords", e.target.value)}
                  placeholder="e.g., family practice, cosmetic, emergency"
                  rows={2}
                  className="bg-white/50 border-slate-200 focus:border-[#006994] focus:ring-1 focus:ring-[#006994] text-slate-900 placeholder:text-slate-400 resize-none"
                  data-testid="keywords-input"
                />
              </div>

              {/* Brand Whitelist */}
              <div className="space-y-2">
                <Label className="text-slate-700 flex items-center gap-2">
                  Brand Whitelist (Optional)
                  <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#006994]/10 text-[#006994] font-semibold">Strict filter</span>
                </Label>
                <Textarea
                  value={formData.brandWhitelist}
                  onChange={(e) => handleChange("brandWhitelist", e.target.value)}
                  placeholder="e.g., Valero, Shell, Exxon, Mobil, Texaco, Phillips 66, 76, BP, Conoco, Marathon, Sunoco"
                  rows={2}
                  className="bg-white/50 border-slate-200 focus:border-[#006994] focus:ring-1 focus:ring-[#006994] text-slate-900 placeholder:text-slate-400 resize-none"
                  data-testid="brand-whitelist-input"
                />
                <p className="text-xs text-slate-500 leading-relaxed">
                  When set, only leads whose <span className="font-semibold">business name contains one of these brands</span> are kept.
                  Case-insensitive whole-word match. Leave blank to keep every result from the selected categories.
                </p>
              </div>

              {/* Info Box */}
              <div className="p-4 rounded-lg bg-[#006994]/5 border border-[#006994]/20">
                <h4 className="text-sm font-medium text-[#006994] mb-2">Data Fields Collected</h4>
                <p className="text-xs text-slate-500">
                  Business Name, Contact Name, Email (via enrichment), Phone, Website, Address, City, State, Zip, Country, Category
                </p>
              </div>

              {/* Submit */}
              <div className="flex gap-4 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate("/dashboard")}
                  className="flex-1 border-slate-200 text-slate-700 hover:bg-slate-100 hover:text-slate-900"
                  data-testid="cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-[#006994] hover:bg-[#005A80] text-white font-bold rounded-sm "
                  data-testid="submit-job-btn"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                      Creating...
                    </span>
                  ) : (
                    <>
                      <MagnifyingGlass size={18} className="mr-2" />
                      Start Lead Search
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default NewJobPage;
